import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "../components/ui/navigation-menu";
import { Button } from "../components/ui/button";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_8a1d6c9e-dd92-45ef-96ba-a4a899e05fe9/artifacts/p4tsc02v_image.png";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { 
      label: 'Services', 
      dropdown: true,
      items: [
        { path: '/services', label: 'All Services' },
        { path: '/timeshare-opc', label: 'Timeshare OPC Marketing' },
        { path: '/lead-generation', label: 'Lead Generation & SEO' },
        { path: '/web-design', label: 'Web Design & Hosting' },
      ]
    },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/careers', label: 'Careers' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 glass border-b border-slate-200/50" data-testid="header">
      <div className="container-max mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src={COMPANY_LOGO} 
              alt="Prestigious Marketing" 
              className="h-10 lg:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            <NavigationMenu>
              <NavigationMenuList>
                {navLinks.map((link) => (
                  link.dropdown ? (
                    <NavigationMenuItem key={link.label}>
                      <NavigationMenuTrigger 
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                          isActive('/services') || isActive('/timeshare-opc') 
                            ? 'text-[#1E3A8A]' 
                            : 'text-slate-700 hover:text-[#1E3A8A]'
                        }`}
                      >
                        {link.label}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid w-[200px] gap-1 p-2">
                          {link.items.map((item) => (
                            <li key={item.path}>
                              <NavigationMenuLink asChild>
                                <Link
                                  to={item.path}
                                  className={`block px-4 py-2 text-sm rounded-md transition-colors ${
                                    isActive(item.path)
                                      ? 'bg-slate-100 text-[#1E3A8A] font-medium'
                                      : 'text-slate-700 hover:bg-slate-50'
                                  }`}
                                  data-testid={`nav-${item.path.replace('/', '')}`}
                                >
                                  {item.label}
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  ) : (
                    <NavigationMenuItem key={link.path}>
                      <Link
                        to={link.path}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                          isActive(link.path) 
                            ? 'text-[#1E3A8A]' 
                            : 'text-slate-700 hover:text-[#1E3A8A]'
                        }`}
                        data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                      >
                        {link.label}
                      </Link>
                    </NavigationMenuItem>
                  )
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/partner">
              <Button 
                className="bg-[#EA580C] hover:bg-[#c2410c] text-white font-medium px-6"
                data-testid="become-partner-btn"
              >
                Become a Partner
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-md text-slate-700 hover:bg-slate-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-200" data-testid="mobile-nav">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                link.dropdown ? (
                  <div key={link.label} className="flex flex-col">
                    <span className="px-4 py-2 text-sm font-medium text-slate-500">
                      {link.label}
                    </span>
                    {link.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`px-6 py-2 text-sm transition-colors ${
                          isActive(item.path)
                            ? 'text-[#1E3A8A] font-medium bg-slate-50'
                            : 'text-slate-700'
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid={`mobile-nav-${item.path.replace('/', '')}`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-2 text-sm transition-colors ${
                      isActive(link.path)
                        ? 'text-[#1E3A8A] font-medium bg-slate-50'
                        : 'text-slate-700'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              <div className="px-4 pt-4 mt-2 border-t border-slate-200">
                <Link to="/partner" onClick={() => setMobileMenuOpen(false)}>
                  <Button 
                    className="w-full bg-[#EA580C] hover:bg-[#c2410c] text-white font-medium"
                    data-testid="mobile-partner-btn"
                  >
                    Become a Partner
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
